import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Container, Icon, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import Form from 'Lib/components/form/Form';
import withForm from 'Lib/components/hoc/withForm';
import SideDrawer, { DRAWER_CONTENT_TYPES } from 'Lib/components/layout/components/SideDrawer';
import SidebarContext from 'Lib/contexts/SidebarContext';
import useApi from 'Lib/hooks/useApi';
import useFormApi from 'Lib/hooks/useFormApi';
import useNotify from 'Lib/hooks/useNotify';
import { InitialLoading } from 'Lib/muiComponents/sidebar/InitialLoading';
import isEmpty from 'Lib/utilities/isEmpty';
import { screenSize } from 'Lib/utilities/screenSize';

function WatchersButton({ toggleDrawer, ...props }) {
    const { mobile } = screenSize();

    return (
        <>
            <Tooltip title="Watchers" placement="top" arrow>
                <Box sx={{ mx: { xs: 0, sm: 2 } }}>
                    {mobile ? (
                        <IconButton onClick={() => toggleDrawer(DRAWER_CONTENT_TYPES.WATCHERS)}>visibility</IconButton>
                    ) : (
                        <IconButton variant="regular" onClick={() => toggleDrawer(DRAWER_CONTENT_TYPES.WATCHERS)}>
                            visibility
                        </IconButton>
                    )}
                </Box>
            </Tooltip>
            <Watchers {...props} />
        </>
    );
}

function Watchers() {
    const [isLoading, setIsLoading] = useState(false);
    const [watchers, setWatchers] = useState([]);

    const { toastSuccess, toastError } = useNotify();
    const { isDrawerOpen, drawerContentTypeID } = useSelector(state => state.ui);
    const { user } = useSelector(state => state.app);
    const {
        sidebar,
        apis,
        components: { User, PropertyManagerUserField }
    } = useContext(SidebarContext);

    const { objectID, objectTypeID } = sidebar;

    // api
    const addWatcher = useFormApi(apis.addWatcher);
    const listWatchers = useApi(apis.listWatchers);
    const deleteWatcher = useApi(apis.deleteWatcher);

    useEffect(() => {
        if (isDrawerOpen && drawerContentTypeID === DRAWER_CONTENT_TYPES.WATCHERS) {
            setIsLoading(true);
            listWatchers(objectTypeID, objectID)
                .then(response => {
                    setWatchers(response.data);
                })
                .catch(error => {
                    toastError(error);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            setWatchers([]);
        }
    }, [isDrawerOpen]);

    const handleDelete = watcherID => {
        setIsLoading(true);

        deleteWatcher(watcherID)
            .then(() => {
                setWatchers(prevState => {
                    return prevState.filter(w => w.watcherID !== watcherID);
                });
                toastSuccess('Successfully deleted watcher');
            })
            .catch(error => {
                toastError(error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleAddWatcher = (userID, form) => {
        setIsLoading(true);

        return addWatcher(form, objectTypeID, objectID, { userID })
            .then(response => {
                setWatchers(prevState => [...prevState, response.data.watcher]);
                toastSuccess('Successfully added watcher');
            })
            .catch(error => {
                toastError(error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const WatchersList = () => {
        if (isEmpty(watchers)) {
            return (
                <Typography color="gray" fontStyle="italic" lineHeight={3}>
                    No watchers yet
                </Typography>
            );
        }

        return (
            <Container
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'common.lightgray',
                    backgroundColor: 'common.lightblue',
                    borderRadius: 1,
                    p: 3,
                    my: 3
                }}
                disableGutters
            >
                {watchers.map((watcher, index) => {
                    return (
                        <Stack key={index} direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography pl={2} maxWidth="200px" sx={{ overflowWrap: 'break-word' }}>
                                <User id={watcher.userID} />
                            </Typography>
                            <IconButton onClick={() => handleDelete(watcher.watcherID)}>close</IconButton>
                        </Stack>
                    );
                })}
            </Container>
        );
    };
    const AddWatchers = () => {
        const handleSubmit = (values, form) => {
            handleAddWatcher(values.userID, form).then(() => {
                form.resetForm();
            });
        };

        return (
            <Form
                onSubmit={handleSubmit}
                render={form => {
                    const hasValue = !!form.values.userID;

                    return (
                        <form onSubmit={form.handleSubmit}>
                            <label>Add Watcher</label>
                            <PropertyManagerUserField id="userID" name="userID" controlled />
                            <Stack direction="row" justifyContent="flex-end">
                                <Button type="submit" disabled={!hasValue}>
                                    Add
                                </Button>
                            </Stack>
                        </form>
                    );
                }}
            />
        );
    };

    const SelfWatcher = () => {
        const selfIsWatching = watchers.some(watcher => watcher.userID == user.userID);

        const StartWatching = () => {
            const icon = <Icon>visibility</Icon>;

            return (
                <Button startIcon={icon} onClick={() => handleAddWatcher(user.userID)}>
                    Start Watching
                </Button>
            );
        };

        const StopWatching = () => {
            const icon = <Icon>visibility_off</Icon>;
            const watcherID = watchers.find(watcher => watcher.userID == user.userID).watcherID;

            return (
                <Button startIcon={icon} onClick={() => handleDelete(watcherID)}>
                    Stop Watching
                </Button>
            );
        };

        return selfIsWatching ? <StopWatching /> : <StartWatching />;
    };

    return (
        <SideDrawer title={'Watchers'} drawerContentTypeID={DRAWER_CONTENT_TYPES.WATCHERS} hideFooter>
            {isLoading ? (
                <InitialLoading />
            ) : (
                <>
                    <SelfWatcher /> <WatchersList /> <AddWatchers />
                </>
            )}
        </SideDrawer>
    );
}

const WatchersWrapper = withForm(WatchersButton);

export { Watchers, WatchersWrapper as WatchersButton };
